import { useEffect } from 'react';
import '../styles/components/LegalPage.css';

const CONTACT_EMAIL = 'gabriel.tulisow@dswsolutions.com.ar';

const PrivacyMedicare = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Política de Privacidad — MediCare | DSW Solutions';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <main className='legal-page'>
      <section className='legal-hero'>
        <div className='legal-hero-bg' />
        <div className='legal-hero-overlay' />
        <div className='legal-hero-content'>
          <span className='legal-eyebrow'>MediCare</span>
          <h1 className='legal-hero-title'>Política de Privacidad</h1>
          <p className='legal-updated'>
            Última actualización: 28 de mayo de 2026
          </p>
        </div>
      </section>

      <article className='legal-body'>
        <h2>1. Identidad del responsable</h2>
        <p>
          MediCare (en adelante, la "Aplicación") es desarrollada y operada por{' '}
          <strong>DSW Solutions SRL</strong>, sociedad constituida en la
          República Argentina, en su carácter de{' '}
          <strong>encargado del tratamiento</strong> de los datos personales que
          los profesionales usuarios cargan en la Aplicación.
        </p>
        <div className='legal-card'>
          <ul>
            <li>
              <strong>Razón social:</strong> DSW Solutions SRL
            </li>
            <li>
              <strong>Email de contacto:</strong>{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </li>
            <li>
              <strong>Sitio web corporativo:</strong>{' '}
              <a href='https://dswsolutions.io'>https://dswsolutions.io</a>
            </li>
            <li>
              <strong>Sitio web del producto:</strong>{' '}
              <a href='https://medicarepro.site'>https://medicarepro.site</a>
            </li>
          </ul>
        </div>

        <h2>2. Roles y alcance</h2>
        <p>
          MediCare es una herramienta de gestión profesional destinada a
          profesionales de la salud (en particular, podólogos) para administrar
          turnos, fichas e información clínica de sus pacientes.
        </p>
        <ul>
          <li>
            <strong>Profesional usuario:</strong> actúa como{' '}
            <strong>responsable del tratamiento</strong> de los datos personales
            y datos de salud de sus pacientes que carga en la Aplicación.
          </li>
          <li>
            <strong>DSW Solutions SRL:</strong> actúa como{' '}
            <strong>encargado del tratamiento</strong>, prestando la
            infraestructura técnica para almacenar y procesar dichos datos
            conforme a las instrucciones del profesional usuario y a esta
            política.
          </li>
        </ul>

        <h2>3. Datos que tratamos</h2>

        <h3>3.1. Datos del profesional usuario (titular de la cuenta)</h3>
        <ul>
          <li>Nombre y apellido.</li>
          <li>Email.</li>
          <li>Contraseña (almacenada en forma cifrada — bcrypt o equivalente).</li>
          <li>
            Token de sesión (almacenado en el dispositivo mediante
            almacenamiento seguro del sistema operativo).
          </li>
        </ul>

        <h3>3.2. Datos de los pacientes (cargados por el profesional usuario)</h3>
        <ul>
          <li>
            Identificación: nombre, apellido, documento, fecha de nacimiento,
            contacto (teléfono, email).
          </li>
          <li>
            Datos clínicos: historia clínica, observaciones, diagnóstico, plan
            de tratamiento.
          </li>
          <li>
            Fotografías clínicas que el profesional decida adjuntar (por ejemplo,
            fotos de lesiones podológicas), capturadas con la cámara del
            dispositivo o seleccionadas desde la galería.
          </li>
          <li>Datos de turnos: fecha, hora, tipo de turno, notas.</li>
        </ul>

        <h3>3.3. Datos técnicos</h3>
        <ul>
          <li>
            Información mínima de funcionamiento (versión de la app, sistema
            operativo, identificador anónimo del dispositivo para sesión).
          </li>
        </ul>

        <p>
          <strong>La Aplicación NO utiliza</strong>: servicios de analytics de
          terceros, herramientas de seguimiento publicitario, redes
          publicitarias, cookies de terceros ni SDKs de telemetría externos.
        </p>

        <h2>4. Finalidad del tratamiento</h2>
        <p>Los datos son tratados exclusivamente para:</p>
        <ul>
          <li>
            Permitir el funcionamiento del servicio (autenticación, gestión de
            turnos y pacientes).
          </li>
          <li>
            Permitir al profesional usuario consultar y editar la información de
            sus pacientes.
          </li>
          <li>Garantizar la seguridad e integridad del servicio.</li>
          <li>Cumplir obligaciones legales aplicables.</li>
        </ul>
        <p>
          <strong>
            No se utilizan los datos con fines publicitarios ni se ceden a
            terceros con fines comerciales.
          </strong>
        </p>

        <h2>5. Base legal</h2>
        <p>El tratamiento se realiza con base en:</p>
        <ul>
          <li>
            El <strong>consentimiento</strong> del profesional usuario al crear
            su cuenta y aceptar esta política.
          </li>
          <li>
            La <strong>relación profesional-paciente</strong> que faculta al
            profesional, en su rol de responsable, a cargar y procesar datos
            clínicos conforme a la normativa sanitaria y de protección de datos
            aplicable (Argentina: Ley 25.326 de Protección de Datos Personales y
            Ley 26.529 de Derechos del Paciente).
          </li>
          <li>
            El <strong>interés legítimo</strong> de garantizar la seguridad del
            servicio.
          </li>
        </ul>

        <h2>6. Conservación</h2>
        <ul>
          <li>
            Los datos del profesional usuario y de sus pacientes se conservan
            mientras la cuenta esté activa.
          </li>
          <li>
            Si el profesional usuario solicita la baja de su cuenta, sus datos y
            los de sus pacientes serán eliminados o anonimizados dentro de los{' '}
            <strong>30 días corridos</strong>, salvo que la legislación aplicable
            obligue a conservarlos por un período mayor (por ejemplo, historia
            clínica: Ley 26.529 exige 10 años).
          </li>
        </ul>

        <h2>7. Destinatarios y terceros</h2>
        <ul>
          <li>
            <strong>
              No vendemos, alquilamos ni compartimos datos personales con
              terceros con fines comerciales o publicitarios.
            </strong>
          </li>
          <li>
            Los datos se almacenan en infraestructura cloud contratada por DSW
            Solutions SRL, que actúa como sub-encargado del tratamiento bajo
            obligaciones contractuales de confidencialidad y seguridad.
          </li>
          <li>
            Podremos compartir datos únicamente cuando exista una obligación
            legal o requerimiento judicial debidamente fundado.
          </li>
        </ul>

        <h2>8. Seguridad</h2>
        <p>
          Implementamos medidas técnicas y organizativas razonables para
          proteger los datos:
        </p>
        <ul>
          <li>Comunicaciones cifradas mediante HTTPS/TLS.</li>
          <li>Contraseñas almacenadas con hashing criptográfico.</li>
          <li>
            Tokens de sesión guardados en el almacenamiento seguro del
            dispositivo (iOS Keychain / Android Keystore).
          </li>
          <li>Control de accesos en la infraestructura.</li>
        </ul>
        <p>
          Ninguna medida de seguridad es absoluta. Recomendamos al profesional
          usuario mantener su dispositivo bloqueado, no compartir credenciales y
          reportar cualquier acceso no autorizado.
        </p>

        <h2>9. Derechos del titular de los datos</h2>
        <p>
          Conforme a la Ley 25.326, los titulares de datos personales tienen
          derecho a:
        </p>
        <ul>
          <li>
            <strong>Acceder</strong> a sus datos personales en posesión de DSW
            Solutions SRL.
          </li>
          <li>
            <strong>Rectificar</strong> datos inexactos o incompletos.
          </li>
          <li>
            <strong>Suprimir</strong> sus datos cuando ya no sean necesarios para
            la finalidad para la cual fueron recolectados.
          </li>
          <li>
            <strong>Oponerse</strong> al tratamiento por motivos legítimos.
          </li>
        </ul>
        <p>
          <strong>Cómo ejercer estos derechos:</strong>
        </p>
        <ul>
          <li>
            <strong>Profesional usuario:</strong> desde la propia aplicación o
            escribiendo a <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </li>
          <li>
            <strong>Paciente:</strong> debe dirigirse en primer término al
            profesional responsable que carga sus datos en la Aplicación. Si
            requiere asistencia técnica complementaria, puede contactarnos a{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </li>
        </ul>
        <p>
          El titular tiene también derecho a presentar denuncias ante la{' '}
          <strong>
            Agencia de Acceso a la Información Pública (AAIP)
          </strong>{' '}
          de Argentina, organismo de control de la Ley 25.326.
        </p>

        <h2>10. Datos de menores</h2>
        <p>
          La Aplicación está destinada a uso profesional por mayores de edad. Si
          un profesional usuario carga datos clínicos de pacientes menores, lo
          hace bajo su responsabilidad y con el consentimiento de los
          responsables legales del menor, conforme a la normativa aplicable.
        </p>

        <h2>11. Transferencias internacionales</h2>
        <p>
          Los datos se almacenan en servidores ubicados en la región configurada
          por DSW Solutions SRL. En caso de que una transferencia internacional
          de datos sea necesaria, se realizará a países con nivel de protección
          adecuado o bajo cláusulas contractuales tipo, conforme a la Ley 25.326
          y disposiciones de la AAIP.
        </p>

        <h2>12. Cambios en esta política</h2>
        <p>
          Podemos actualizar esta política para reflejar cambios legales o de
          funcionalidad. Notificaremos los cambios sustanciales dentro de la
          Aplicación o por correo electrónico. La versión vigente estará siempre
          publicada en{' '}
          <a href='https://dswsolutions.io/productos/medicare/privacidad'>
            https://dswsolutions.io/productos/medicare/privacidad
          </a>
          .
        </p>

        <h2>13. Contacto</h2>
        <div className='legal-card'>
          <p>
            <strong>DSW Solutions SRL</strong>
            <br />
            Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            <br />
            Sitio web corporativo:{' '}
            <a href='https://dswsolutions.io'>https://dswsolutions.io</a>
            <br />
            Sitio web del producto:{' '}
            <a href='https://medicarepro.site'>https://medicarepro.site</a>
          </p>
        </div>

        <h2>14. Ley aplicable</h2>
        <p>
          Esta política se rige por la legislación de la{' '}
          <strong>República Argentina</strong>, en particular por la{' '}
          <strong>Ley 25.326 de Protección de Datos Personales</strong>, su
          decreto reglamentario y las disposiciones complementarias dictadas por
          la Agencia de Acceso a la Información Pública.
        </p>

        <div className='legal-footer'>
          © DSW Solutions SRL — Todos los derechos reservados.
        </div>
      </article>
    </main>
  );
};

export default PrivacyMedicare;
